import { useMutation, useQuery, useQueryClient } from 'react-query'
import { mapToBilling, mapToPaymentMethods } from 'api/mappers/paymentMethod'
import { useApiClient } from 'contexts/ApiClientContext'
import {
  CreatePaymentMethodRequest,
  DeletePaymentMethodRequest,
  GetPaymentMethodsRequest,
  SelectPaymentMethodRequest,
  CreateSetupIntentRequest,
  PaymentMethod,
  SetupIntent,
} from 'lib/generated/api'
import { useHandleApiError } from 'api/utils'

export const QUERY_KEYS = {
  paymentMethods: 'paymentMethods',
} as const

export const paymentMethodsQueryKey = ({
  workspaceId,
  ...queryParams
}: GetPaymentMethodsRequest) => [
  QUERY_KEYS.paymentMethods,
  workspaceId,
  queryParams,
]

export const useGetPaymentMethods = (request: GetPaymentMethodsRequest) => {
  const { paymentMethodsApi } = useApiClient()

  return useQuery(
    paymentMethodsQueryKey(request),
    () => paymentMethodsApi.getPaymentMethods(request),
    {
      enabled: false,
      select: mapToPaymentMethods,
    }
  )
}

export const useGetBillingQuery = (request: GetPaymentMethodsRequest) => {
  const { paymentMethodsApi } = useApiClient()

  return useQuery(
    paymentMethodsQueryKey(request),
    () => paymentMethodsApi.getPaymentMethods(request),
    {
      enabled: !!request.workspaceId,
      select: mapToBilling,
    }
  )
}

export const useCreatePaymentMethod = () => {
  const queryClient = useQueryClient()
  const { paymentMethodsApi } = useApiClient()
  const onError = useHandleApiError({ showMessage: false })

  queryClient.setMutationDefaults('createPaymentMethod', {
    mutationFn: (request: CreatePaymentMethodRequest) =>
      paymentMethodsApi.createPaymentMethod(request),
    onError,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(
        paymentMethodsQueryKey({
          workspaceId: variables.workspaceId,
        })
      )
    },
  })

  return useMutation<PaymentMethod, unknown, CreatePaymentMethodRequest>(
    'createPaymentMethod'
  )
}

export const useDeletePaymentMethod = () => {
  const queryClient = useQueryClient()
  const { paymentMethodsApi } = useApiClient()

  return useMutation(
    (request: DeletePaymentMethodRequest) =>
      paymentMethodsApi.deletePaymentMethod(request),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(
          paymentMethodsQueryKey({
            workspaceId: variables.workspaceId,
          })
        )
      },
    }
  )
}

export const usePaymentMethodSelect = () => {
  const queryClient = useQueryClient()
  const { paymentMethodsApi } = useApiClient()

  return useMutation(
    (request: SelectPaymentMethodRequest) =>
      paymentMethodsApi.selectPaymentMethod(request),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(
          paymentMethodsQueryKey({
            workspaceId: variables.workspaceId,
          })
        )
      },
    }
  )
}

export const useCreateSetupIntent = () => {
  const queryClient = useQueryClient()
  const { paymentMethodsApi } = useApiClient()
  const onError = useHandleApiError({ showMessage: false })

  queryClient.setMutationDefaults('createSetupIntent', {
    mutationFn: (request: CreateSetupIntentRequest) =>
      paymentMethodsApi.createSetupIntent(request),
    onError,
  })

  return useMutation<SetupIntent, unknown, CreateSetupIntentRequest>(
    'createSetupIntent'
  )
}
